import { useEffect, useRef } from 'react';
import ImageProtectionService from '../services/imageProtectionService';
import RGPDService from '../services/rgpdService';

// Hook pour appliquer automatiquement la protection des images (DÉSACTIVÉ)
export const useImageProtection = () => {
  useEffect(() => {
    console.log("⚠️ Hook de protection d'images désactivé");

    // Supprimer toutes les protections existantes sur les images
    const removeExistingProtections = () => {
      const images = document.querySelectorAll('img[data-protected="true"]');
      images.forEach((img) => {
        // Supprimer l'attribut de protection
        img.removeAttribute('data-protected');

        // Rétablir les comportements normaux
        img.style.userSelect = 'auto';
        img.style.webkitUserSelect = 'auto';
        img.style.mozUserSelect = 'auto';
        img.style.msUserSelect = 'auto';
        img.draggable = true;

        // Supprimer les watermarks existants
        const container = img.parentElement;
        if (container) {
          const existingWatermarks = container.querySelectorAll('.watermark, .css-watermark');
          existingWatermarks.forEach((watermark) => watermark.remove());

          const wrapper = container.querySelector('.image-watermark-wrapper');
          if (wrapper) {
            const parent = wrapper.parentElement;
            if (parent) {
              parent.insertBefore(img, wrapper);
              wrapper.remove();
            }
          }
        }
      });
    };

    // Supprimer les protections existantes immédiatement
    removeExistingProtections();

    // Observer les nouvelles images et s'assurer qu'aucune protection n'est appliquée
    const observer = new MutationObserver(() => {
      removeExistingProtections();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []); // Pas de dépendances pour éviter les re-exécutions

  // Fonction pour appliquer la protection (désactivée)
  const applyProtection = (imageElement) => {
    console.log('⚠️ Application protection désactivée pour image:', imageElement);
    return false;
  };

  // Fonction pour supprimer la protection
  const removeProtection = (imageElement) => {
    if (imageElement) {
      imageElement.removeAttribute('data-protected');
      imageElement.style.userSelect = 'auto';
      imageElement.style.webkitUserSelect = 'auto';
      imageElement.style.mozUserSelect = 'auto';
      imageElement.style.msUserSelect = 'auto';
      imageElement.draggable = true;

      const container = imageElement.parentElement;
      if (container) {
        const watermarks = container.querySelectorAll('.watermark, .css-watermark');
        watermarks.forEach((w) => w.remove());
      }
    }
    return true;
  };

  return {
    applyProtection,
    removeProtection,
    isProtectionEnabled: false, // Toujours désactivé
  };
};

// Hook pour gérer les consentements RGPD
export const useRGPDConsent = () => {
  const getConsent = () => RGPDService.getConsent();
  const hasConsent = (type) => RGPDService.hasConsent(type);

  return {
    consent: getConsent(),
    hasConsent,
    saveConsent: RGPDService.saveConsent,
    revokeConsent: RGPDService.revokeConsent,
    shouldShowBanner: RGPDService.shouldShowBanner(),
    exportUserData: RGPDService.exportUserData,
    deleteAllUserData: RGPDService.deleteAllUserData,
  };
};

// Hook pour la gestion des droits d'image des artistes
export const useArtistImageRights = () => {
  return {
    saveArtistRights: RGPDService.saveArtistImageRights,
    getImageRights: RGPDService.getImageRights,
    hasAuthorization: RGPDService.hasImageRightsAuthorization,
    revokeRights: RGPDService.revokeArtistImageRights,
  };
};

// Hook personnalisé pour une image protégée spécifique
export const useProtectedImage = (imageRef, options = {}) => {
  const applied = useRef(false);

  useEffect(() => {
    const imageElement = imageRef.current;

    if (imageElement && !applied.current) {
      const applyProtection = () => {
        ImageProtectionService.protectImage(imageElement, options);
        applied.current = true;
      };

      if (imageElement.complete) {
        applyProtection();
      } else {
        imageElement.addEventListener('load', applyProtection);
        return () => imageElement.removeEventListener('load', applyProtection);
      }
    }
  }, [imageRef, options]);

  return applied.current;
};

// Hook pour initialiser les protections globales
export const useGlobalProtection = () => {
  useEffect(() => {
    // Initialiser les protections globales
    ImageProtectionService.initGlobalProtections();

    // Log de l'initialisation
    RGPDService.logRGPDAction('global_protection_initialized');
  }, []);
};

export default {
  useImageProtection,
  useRGPDConsent,
  useArtistImageRights,
  useProtectedImage,
  useGlobalProtection,
};
